import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Video, CheckCircle2, XCircle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function VideoReview() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const [rejecting, setRejecting] = useState("");
  const [reason, setReason] = useState("");

  const load = () => api.get("/admin/video-queue").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/campaigns/${id}/video/approve`, { notes: "Approved after content review" });
      toast.success("Video approved — it's now visible on the campaign.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const reject = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/campaigns/${id}/video/reject`, { notes: reason || "" });
      toast.success("Video rejected.");
      setRejecting(""); setReason("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  if (!items) return <InlineLoader label="Loading video queue" />;

  return (
    <div className="mx-auto max-w-4xl" data-testid="video-review-page">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Video Review</h1>
      <p className="mt-1 text-sm text-ff-medium">Review organizer-submitted videos for inappropriate content before they appear publicly.</p>

      {items.length === 0 ? (
        <div className="mt-8">
          <EmptyState icon={Video} title="No videos to review" message="New campaign video submissions will appear here." testid="video-review-empty" />
        </div>
      ) : (
        <div className="mt-6 space-y-5" data-testid="video-review-list">
          {items.map((c) => (
            <div key={c.id} className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid={`video-review-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/fundraisers/${c.slug}`} target="_blank" className="font-heading font-bold text-ff-blue hover:text-ff-red">
                    {c.title} <ExternalLink className="ml-1 inline h-3.5 w-3.5" />
                  </Link>
                  <div className="mt-0.5 text-xs text-ff-medium">
                    by {c.organizer_name || "Organizer"} · {c.organizer_email}
                    {c.use_video_cover && <span className="ml-2 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[10px] font-bold text-[#1D4ED8]">SET AS COVER</span>}
                  </div>
                </div>
                <span className="rounded-full bg-[#FFF7ED] px-2.5 py-1 text-xs font-bold uppercase text-ff-amber">Pending</span>
              </div>

              {c.video_url && (
                <video src={c.video_url} controls className="mt-4 max-h-96 w-full rounded-xl bg-black" data-testid={`video-player-${c.id}`} />
              )}

              {rejecting === c.id ? (
                <div className="mt-4 space-y-2">
                  <Textarea data-testid={`video-reject-reason-${c.id}`} rows={2} value={reason}
                    onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection (shared with the organizer)…" />
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy === c.id} onClick={() => reject(c.id)} data-testid={`video-confirm-reject-${c.id}`}
                      className="bg-ff-red text-white hover:bg-ff-red/90">
                      {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm rejection"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setRejecting(""); setReason(""); }}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button disabled={busy === c.id} onClick={() => approve(c.id)} data-testid={`video-approve-${c.id}`}
                    className="bg-ff-action text-white hover:bg-ff-actionDark">
                    {busy === c.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Approve
                  </Button>
                  <Button variant="outline" onClick={() => { setRejecting(c.id); setReason(""); }} data-testid={`video-reject-${c.id}`}
                    className="border-ff-red text-ff-red hover:bg-ff-red hover:text-white">
                    <XCircle className="mr-2 h-4 w-4" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
